import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, SCIcon, Dialog, ProgressBar } from '../../Common/Imports';
import { ApiResponse, Brands_EXT } from '../../Models/Classes';
import BrandNew from './Brands_New';
import BrandEdit from './Brands_Edit';
import { PermissionHelper, usePageID, usePermissions } from '../../Helpers/Permissions/PermissionContext';
import { FormatDateTimeFromDate } from '../../Common/Utility/FormatDateTimeFromDate';

interface BrandListProps {
    stakeholderId: number;
}

export const BrandList: React.FC<BrandListProps> = ({ stakeholderId }) => {
    const { pageID } = usePageID();
    const PAGENAME = "-BRANDS";
    const P_Brand_Create = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 1 };
    const P_Brand_Read = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 2 };
    const P_Brand_Update = { idAspNetRole: usePermissions().userRole, idItem: pageID + PAGENAME, idPermission: 3 };

    const AxiosHelper = useAxios();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [TableData, SetTableData] = useState<Brands_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [editData, setEditData] = useState<Brands_EXT | undefined>();
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {

            const resp = await AxiosHelper.get<ApiResponse<Brands_EXT[]>>(`Brand/GetList?StakeholderId=${stakeholderId}`);
            if (resp.data.code == 0) {
                const processedData = resp.data.data.map(item => ({
                    ...item,
                    created: item.created ? new Date(item.created) : undefined,
                    updated: item.updated ? new Date(item.updated) : undefined
                }));
                SetTableData(processedData);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, stakeholderId]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const updateStatus = async (data: Brands_EXT) => {
        data.isActive = !data.isActive;
        try {
            const resp = await AxiosHelper.post<ApiResponse<Brands_EXT>>(`Brand/UpdateBrand/`, data);
            if (resp.data.code == 0) {
                showToast('success', 'Success', t("Common.Toasts.New.Success"));
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            GetData();
        }
    }

    const activeBody = (data: Brands_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Brand_Update}>
                {
                    !data.isActive ?
                        <SCIcon Name="Lock" Classes="text-danger" Pointer OnClick={() => updateStatus(data)} />
                        :
                        <SCIcon Name="Unlock" Classes="text-success" Pointer OnClick={() => updateStatus(data)} />
                }
            </PermissionHelper>);
    }

    const openEdit = (data: Brands_EXT) => {
        setEditData(data);
    }

    const editBody = (data: Brands_EXT) => {
        return (
            <PermissionHelper requiredPermission={P_Brand_Update}>
                <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => openEdit(data)} />
            </PermissionHelper>);
    }


    return (
        <PermissionHelper requiredPermission={P_Brand_Read}>
            {
                CreateNewData &&
                <Dialog
                    header="New Brand"
                    visible={CreateNewData ? true : false}
                    onHide={() => { setCreateNewData(false); GetData() }}>
                    <BrandNew stakeholderId={stakeholderId} OnSave={() => { setCreateNewData(false); GetData() }} />
                </Dialog>
            }
            {
                editData &&
                <Dialog
                    header="Edit Brand"
                    visible={editData ? true : false}
                    onHide={() => { setEditData(undefined); GetData() }}>
                    <BrandEdit IdBrand={editData.idBrand} OnSave={() => { setEditData(undefined); GetData() }} />
                </Dialog>
            }
            <PermissionHelper requiredPermission={P_Brand_Create}>
                <div className="col d-flex justify-content-end">
                    <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
                </div>
            </PermissionHelper>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        value={TableData}
                        size="small"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        stripedRows>
                        <Column
                            field="name"
                            header={t("Brand.List.Name")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="emails"
                            header={t("Brand.List.Email")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="phone"
                            header={t("Brand.List.Phone")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="providerBrand"
                            header={t("Brand.List.ProviderBrand")}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="isDefault"
                            header={t("Brand.List.IsDefault")}
                            body={(e: Brands_EXT) => { const _T = e.isDefault ? t("Common.Booleans.Yes") : t("Common.Booleans.No"); return _T }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="created"
                            header={t("Brand.List.Created")}
                            body={(e: Brands_EXT) => FormatDateTimeFromDate(e.created, { withTime: true })} />
                        <Column
                            field="updated"
                            header={t("Brand.List.Updated")}
                            body={(e: Brands_EXT) => FormatDateTimeFromDate(e.updated, { withTime: true })}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="isActive"
                            header={t("Contacts.Status")}
                            style={{ maxWidth: '150px!important', width: "150px" }}
                            body={(e: Brands_EXT) => { const _T = e.isActive ? t("Common.Booleans.Active") : t("Common.Booleans.NotActive"); return _T }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            body={(e: Brands_EXT) => editBody(e)} style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                        <Column
                            field="active"
                            body={(e: Brands_EXT) => activeBody(e)} style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </PermissionHelper>
    );
};

export default BrandList;
